import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit3, FiMousePointer, FiRotateCcw, FiRotateCw, FiUploadCloud, FiZoomIn, FiZoomOut } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { invalidate } from "@react-three/fiber";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { BONI_CHECKBOX_CLASSNAME } from "../../constants/brandColors";
import { BackdropImageType } from "../../types/floorplannerTypes";
import {
  DeleteFloorplanBackdropImageDocument,
  UploadFloorplanBackdropImageDocument,
} from "../../gql/floorplanBackdrop";
import { AddUserItemDocument } from "../../gql/userItem";

type Mode = "add" | "replace" | "recalibrate" | "erase";
type StepId = "upload" | "start" | "end" | "distance" | "erase";

interface Point {
  x: number;
  y: number;
}

interface BackdropImageModalProps {
  isOpen: boolean;
  mode: Mode;
  backdrop?: BackdropImageType;
  onClose: () => void;
}

const containerWidth = 700;
const containerHeight = 350;

const canvasToFile = (canvas: HTMLCanvasElement, filename: string): Promise<File> =>
  new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Could not create edited image"));
          return;
        }
        resolve(new File([blob], filename, { type: "image/png" }));
      }, "image/png");
    } catch (error) {
      reject(error);
    }
  });

const loadImageElement = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    if (!src.startsWith("data:")) {
      image.crossOrigin = "anonymous";
    }
    image.src = src;
  });

const createFinalizedBackdropCanvas = (sourceCanvas: HTMLCanvasElement) => {
  const sourceContext = sourceCanvas.getContext("2d");
  if (!sourceContext) return sourceCanvas;

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = sourceCanvas.width;
  outputCanvas.height = sourceCanvas.height;
  const outputContext = outputCanvas.getContext("2d");
  if (!outputContext) return sourceCanvas;

  const imageData = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
  for (let i = 0; i < imageData.data.length; i += 4) {
    const alpha = imageData.data[i + 3];
    if (alpha < 192) {
      imageData.data[i] = 255;
      imageData.data[i + 1] = 255;
      imageData.data[i + 2] = 255;
      imageData.data[i + 3] = 0;
    } else {
      imageData.data[i + 3] = 255;
    }
  }

  outputContext.putImageData(imageData, 0, 0);
  return outputCanvas;
};

const createAlphaMaskDataUrl = (canvas: HTMLCanvasElement) => {
  const context = canvas.getContext("2d");
  if (!context) return undefined;

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const maskData = context.createImageData(canvas.width, canvas.height);

  for (let i = 0; i < imageData.data.length; i += 4) {
    const visible = imageData.data[i + 3] >= 192 ? 255 : 0;
    maskData.data[i] = visible;
    maskData.data[i + 1] = visible;
    maskData.data[i + 2] = visible;
    maskData.data[i + 3] = 255;
  }

  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = canvas.width;
  maskCanvas.height = canvas.height;
  maskCanvas.getContext("2d")?.putImageData(maskData, 0, 0);
  return maskCanvas.toDataURL("image/png");
};

const applyAlphaMaskToCanvas = async (canvas: HTMLCanvasElement, alphaMask?: string) => {
  if (!alphaMask) return;

  const context = canvas.getContext("2d");
  if (!context) return;

  const maskImage = await loadImageElement(alphaMask);
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = canvas.width;
  maskCanvas.height = canvas.height;
  const maskContext = maskCanvas.getContext("2d", { willReadFrequently: true });
  if (!maskContext) return;

  maskContext.drawImage(maskImage, 0, 0, canvas.width, canvas.height);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const maskData = maskContext.getImageData(0, 0, canvas.width, canvas.height);

  for (let i = 0; i < imageData.data.length; i += 4) {
    const visible = (maskData.data[i] + maskData.data[i + 1] + maskData.data[i + 2]) / 3 >= 192;
    if (!visible) {
      imageData.data[i] = 255;
      imageData.data[i + 1] = 255;
      imageData.data[i + 2] = 255;
      imageData.data[i + 3] = 0;
    } else {
      imageData.data[i + 3] = 255;
    }
  }

  context.putImageData(imageData, 0, 0);
};

const formatMillimeters = (value?: number, notSet = "Not set") => {
  if (!Number.isFinite(value) || !value || value <= 0) return notSet;
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} mm`;
};

const formatPercent = (value?: number, notSet = "Not set") => {
  if (!Number.isFinite(value) || !value || value <= 0) return notSet;
  return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

const getClient = () => {
  const client = floorplannerStore.client ?? editorStore.client;
  if (!client) {
    throw new Error("Apollo client not initialized");
  }
  floorplannerStore.client = client;
  return client;
};

const deleteUploadedBackdrop = async (floorplanId: string, imageUrl: string) => {
  try {
    await getClient().mutate({
      mutation: DeleteFloorplanBackdropImageDocument,
      variables: { floorplanId, imageUrl },
    });
  } catch (error) {
    console.warn("Could not delete floorplan backdrop image", error);
  }
};

export const BackdropImageModal: React.FC<BackdropImageModalProps> = ({
  isOpen,
  mode,
  backdrop,
  onClose,
}) => {
  const { t } = useTranslation();
  const [backdropImage, setBackdropImage] = useState<string | null>(null);
  const [backdropFile, setBackdropFile] = useState<File | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [endPoint, setEndPoint] = useState<Point | null>(null);
  const [measurementValue, setMeasurementValue] = useState("100");
  const [isDragging, setIsDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState<"start" | "end" | null>(null);
  const [cursorPos, setCursorPos] = useState<Point | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [activeStep, setActiveStep] = useState<StepId>("upload");
  const [eraserSize, setEraserSize] = useState(36);
  const [hasErased, setHasErased] = useState(false);
  const [editHistory, setEditHistory] = useState({ index: 0, length: 0 });
  const [eraserCursorPoint, setEraserCursorPoint] = useState<Point | null>(null);
  const [eraserFillMode, setEraserFillMode] = useState<"transparent" | "color">("transparent");
  const [eraserFillColor, setEraserFillColor] = useState("#ffffff");
  const [addToUploads, setAddToUploads] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const eraserColorInputRef = useRef<HTMLInputElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isErasingRef = useRef(false);
  const lastErasePointRef = useRef<Point | null>(null);
  const editHistoryRef = useRef<{ entries: ImageData[]; index: number }>({ entries: [], index: 0 });
  const strokeChangedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setBackdropImage(mode === "recalibrate" || mode === "erase" ? backdrop?.backdropImagePath ?? null : null);
    setBackdropFile(null);
    setZoomLevel(1);
    setStartPoint(null);
    setEndPoint(null);
    setMeasurementValue(String(backdrop?.backdropSize ?? 100));
    setIsDragging(false);
    setDragPoint(null);
    setCursorPos(null);
    setIsEditingLabel(false);
    setImageSize({ width: 0, height: 0 });
    setEraserSize(36);
    setHasErased(false);
    setEditHistory({ index: 0, length: 0 });
    setEraserCursorPoint(null);
    setEraserFillMode("transparent");
    setEraserFillColor("#ffffff");
    setAddToUploads(false);
    editHistoryRef.current = { entries: [], index: 0 };
    strokeChangedRef.current = false;
    setActiveStep(
      mode === "erase" && backdrop?.backdropImagePath
        ? "erase"
        : mode === "recalibrate" && backdrop?.backdropImagePath
          ? "start"
          : "upload"
    );
  }, [isOpen, mode, backdrop?.id, backdrop?.backdropImagePath, backdrop?.backdropSize]);

  useEffect(() => {
    if (!backdropImage || !canvasRef.current) return;

    let isActive = true;

    loadImageElement(backdropImage).then(async (image) => {
      if (!isActive || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.globalCompositeOperation = "source-over";
      context.drawImage(image, 0, 0);
      await applyAlphaMaskToCanvas(canvas, mode === "erase" ? backdrop?.backdropAlphaMask : undefined);
      if (!isActive) return;
      setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
      try {
        const initialImageData = context.getImageData(0, 0, canvas.width, canvas.height);
        editHistoryRef.current = { entries: [initialImageData], index: 0 };
        setEditHistory({ index: 0, length: 1 });
        setHasErased(false);
      } catch (error) {
        console.error("Error preparing backdrop edit history", error);
        editHistoryRef.current = { entries: [], index: 0 };
        setEditHistory({ index: 0, length: 0 });
      }
    }).catch((error) => {
      console.error("Error loading backdrop image for editing", error);
    });

    return () => {
      isActive = false;
    };
  }, [backdropImage, mode, backdrop?.backdropAlphaMask]);

  const getImageLayout = () => {
    const viewportWidth = imageContainerRef.current?.clientWidth ?? containerWidth;
    const viewportHeight = imageContainerRef.current?.clientHeight ?? containerHeight;
    const scaledWidth = imageSize.width * zoomLevel;
    const scaledHeight = imageSize.height * zoomLevel;
    const stageWidth = Math.max(viewportWidth, scaledWidth);
    const stageHeight = Math.max(viewportHeight, scaledHeight);
    return {
      scaledWidth,
      scaledHeight,
      stageWidth,
      stageHeight,
      offsetX: (stageWidth - scaledWidth) / 2,
      offsetY: (stageHeight - scaledHeight) / 2,
    };
  };

  const toImageCoords = (containerX: number, containerY: number) => {
    const { offsetX, offsetY } = getImageLayout();
    const scrollLeft = imageContainerRef.current?.scrollLeft ?? 0;
    const scrollTop = imageContainerRef.current?.scrollTop ?? 0;
    return {
      x: (containerX + scrollLeft - offsetX) / zoomLevel,
      y: (containerY + scrollTop - offsetY) / zoomLevel,
    };
  };

  const toContainerCoords = (imageX: number, imageY: number) => {
    const { offsetX, offsetY } = getImageLayout();
    return {
      x: offsetX + imageX * zoomLevel,
      y: offsetY + imageY * zoomLevel,
    };
  };

  const snapTo45 = (start: Point, current: Point) => {
    const dx = current.x - start.x;
    const dy = current.y - start.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);
    const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
    return {
      x: start.x + length * Math.cos(snappedAngle),
      y: start.y + length * Math.sin(snappedAngle),
    };
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!imageContainerRef.current || !backdropImage) return;
      const rect = imageContainerRef.current.getBoundingClientRect();
      const containerX = event.clientX - rect.left;
      const containerY = event.clientY - rect.top;
      let rawPos = toImageCoords(containerX, containerY);

      if (isDragging && dragPoint) {
        if (dragPoint === "start") {
          setStartPoint(rawPos);
          setActiveStep("end");
        } else {
          if (startPoint && !event.shiftKey) {
            rawPos = snapTo45(startPoint, rawPos);
          }
          setEndPoint(rawPos);
          setActiveStep("distance");
        }
      } else {
        if (startPoint && !endPoint && !event.shiftKey) {
          rawPos = snapTo45(startPoint, rawPos);
        }
        setCursorPos(rawPos);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragPoint(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [backdropImage, dragPoint, endPoint, imageSize.height, imageSize.width, isDragging, startPoint, zoomLevel]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const getPointerImageCoords = (clientX: number, clientY: number) => {
    if (!imageContainerRef.current) return null;
    const rect = imageContainerRef.current.getBoundingClientRect();
    return toImageCoords(clientX - rect.left, clientY - rect.top);
  };

  const handleImageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (activeStep === "erase") return;
    if (!backdropImage) return;
    let clickPos = getPointerImageCoords(event.clientX, event.clientY);
    if (!clickPos) return;

    if (activeStep === "start" || !startPoint) {
      setCursorPos(clickPos);
      setStartPoint(clickPos);
      setEndPoint(null);
      setIsEditingLabel(false);
      setActiveStep("end");
      return;
    }

    if ((activeStep === "end" || !endPoint) && startPoint) {
      if (!event.shiftKey) {
        clickPos = snapTo45(startPoint, clickPos);
      }
      setCursorPos(clickPos);
      setEndPoint(clickPos);
      setIsEditingLabel(true);
      setActiveStep("distance");
    }
  };

  const handleStepClick = (stepId: StepId) => {
    if (stepId === "erase") {
      if (backdropImage) {
        setActiveStep("erase");
        setIsEditingLabel(false);
      }
      return;
    }

    if (stepId === "upload") {
      setActiveStep("upload");
      if (mode !== "recalibrate") {
        fileInputRef.current?.click();
      }
      return;
    }

    if (!backdropImage) return;

    if (stepId === "start") {
      setStartPoint(null);
      setEndPoint(null);
      setCursorPos(null);
      setIsEditingLabel(false);
      setActiveStep("start");
      return;
    }

    if (stepId === "end") {
      if (!startPoint) {
        setActiveStep("start");
        return;
      }
      setEndPoint(null);
      setCursorPos(null);
      setIsEditingLabel(false);
      setActiveStep("end");
      return;
    }

    if (!startPoint) {
      setActiveStep("start");
      return;
    }

    if (!endPoint) {
      setActiveStep("end");
      return;
    }

    setActiveStep("distance");
    setIsEditingLabel(true);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBackdropFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      setBackdropImage(evt.target?.result as string);
      setActiveStep("start");
    };
    reader.readAsDataURL(file);
    setStartPoint(null);
    setEndPoint(null);
    setIsEditingLabel(false);
    setHasErased(false);
  };

  const getLineStyle = (point: Point) => {
    if (!startPoint) return {};
    const cStart = toContainerCoords(startPoint.x, startPoint.y);
    const cEnd = toContainerCoords(point.x, point.y);
    const dx = cEnd.x - cStart.x;
    const dy = cEnd.y - cStart.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const shouldFlip = angle > 90 || angle < -90;
    return {
      width: `${length}px`,
      transform: `rotate(${angle}deg)`,
      transformOrigin: "0 50%",
      position: "absolute" as const,
      left: `${cStart.x}px`,
      top: `${cStart.y}px`,
      "--label-offset-y": shouldFlip ? "calc(-100% - 15px)" : "calc(100% + 5px)",
      "--label-rotation": shouldFlip ? "180deg" : "0deg",
    };
  };

  const updateEditHistoryState = () => {
    const history = editHistoryRef.current;
    setEditHistory({ index: history.index, length: history.entries.length });
    setHasErased(history.index > 0);
  };

  const getCurrentCanvasImageData = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return null;
    try {
      return context.getImageData(0, 0, canvas.width, canvas.height);
    } catch (error) {
      console.error("Error reading backdrop edit history", error);
      return null;
    }
  };

  const restoreCanvasImageData = (imageData: ImageData) => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.putImageData(imageData, 0, 0);
  };

  const commitEditHistory = () => {
    const imageData = getCurrentCanvasImageData();
    if (!imageData) return;

    const history = editHistoryRef.current;
    const entries = history.entries.slice(0, history.index + 1);
    entries.push(imageData);

    if (entries.length > 30) {
      entries.shift();
    }

    editHistoryRef.current = {
      entries,
      index: entries.length - 1,
    };
    updateEditHistoryState();
  };

  const undoEdit = () => {
    const history = editHistoryRef.current;
    if (history.index <= 0) return;
    history.index -= 1;
    restoreCanvasImageData(history.entries[history.index]);
    updateEditHistoryState();
  };

  const redoEdit = () => {
    const history = editHistoryRef.current;
    if (history.index >= history.entries.length - 1) return;
    history.index += 1;
    restoreCanvasImageData(history.entries[history.index]);
    updateEditHistoryState();
  };

  const eraseLine = (from: Point, to: Point) => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    context.save();
    context.globalCompositeOperation = eraserFillMode === "transparent" ? "destination-out" : "source-over";
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = eraserSize;
    if (eraserFillMode === "color") {
      context.strokeStyle = eraserFillColor;
      context.fillStyle = eraserFillColor;
    }
    context.beginPath();
    context.moveTo(from.x, from.y);
    context.lineTo(to.x, to.y);
    context.stroke();
    context.beginPath();
    context.arc(to.x, to.y, eraserSize / 2, 0, Math.PI * 2);
    context.fill();
    context.restore();
    strokeChangedRef.current = true;
  };

  const handleErasePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!backdropImage || activeStep !== "erase") return;
    const point = getPointerImageCoords(event.clientX, event.clientY);
    if (!point) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    isErasingRef.current = true;
    strokeChangedRef.current = false;
    lastErasePointRef.current = point;
    setEraserCursorPoint(point);
    eraseLine(point, point);
  };

  const handleErasePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activeStep !== "erase") return;
    const point = getPointerImageCoords(event.clientX, event.clientY);
    if (point) setEraserCursorPoint(point);
    if (!isErasingRef.current) return;
    const previousPoint = lastErasePointRef.current;
    if (!point || !previousPoint) return;

    event.preventDefault();
    event.stopPropagation();
    eraseLine(previousPoint, point);
    lastErasePointRef.current = point;
  };

  const handleErasePointerEnd = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (isErasingRef.current && strokeChangedRef.current) {
      commitEditHistory();
    }
    isErasingRef.current = false;
    strokeChangedRef.current = false;
    lastErasePointRef.current = null;
    if (event.type === "pointerleave" || event.type === "pointercancel") {
      setEraserCursorPoint(null);
    }
  };

  const getFinalizedEditedCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width || !canvas.height) {
      throw new Error("Edited image is not ready");
    }
    return createFinalizedBackdropCanvas(canvas);
  };

  const getEditedImageFile = async () => {
    return canvasToFile(getFinalizedEditedCanvas(), `floorplan_backdrop_${Date.now()}.png`);
  };

  const getCalibration = () => {
    if (!startPoint || !endPoint || !imageSize.width) {
      throw new Error("Select a reference measurement first");
    }
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);
    const backdropSize = parseFloat(measurementValue);
    if (!Number.isFinite(backdropSize) || backdropSize <= 0) {
      throw new Error("Enter a valid measurement");
    }
    return {
      backdropRatio: pixelDistance / imageSize.width,
      backdropSize,
    };
  };

  const handleDone = async () => {
    setIsLoading(true);
    try {
      if (mode === "erase" && backdrop) {
        const floorplanId = await floorplannerStore.ensureCloudFloorplanId();
        const editedCanvas = getFinalizedEditedCanvas();
        const alphaMask = createAlphaMaskDataUrl(editedCanvas);
        const imageFile = await canvasToFile(editedCanvas, `floorplan_backdrop_${Date.now()}.png`);
        const oldImageUrl = backdrop.backdropImagePath;
        const uploadResult = await getClient().mutate({
          mutation: UploadFloorplanBackdropImageDocument,
          variables: { floorplanId, image: imageFile },
        });
        const imageUrl = uploadResult.data?.uploadFloorplanBackdropImage;
        if (!imageUrl) {
          throw new Error("Failed to upload edited backdrop image");
        }

        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropImagePath", imageUrl);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropAlphaMask", alphaMask);
        floorplannerStore.persistStore();
        if (oldImageUrl && oldImageUrl !== imageUrl) {
          await deleteUploadedBackdrop(floorplanId, oldImageUrl);
        }
        invalidate();
        onClose();
        return;
      }

      const { backdropRatio, backdropSize } = getCalibration();
      const floorplanId = await floorplannerStore.ensureCloudFloorplanId();

      if (mode === "recalibrate" && backdrop) {
        floorplannerStore.beginUndoTransaction("recalibrateBackdropImage");
        floorplannerStore.recordSymbolForUndo(backdrop.id);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropRatio", backdropRatio);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropSize", backdropSize);
        floorplannerStore.recordSymbolChange(backdrop.id, "modify");
        floorplannerStore.pushToUndoStack();
        invalidate();
        onClose();
        return;
      }

      if (!backdropFile && !hasErased) {
        throw new Error("Select a backdrop image first");
      }

      const imageFile = hasErased ? await getEditedImageFile() : backdropFile;
      if (!imageFile) {
        throw new Error("Select a backdrop image first");
      }

      const uploadResult = await getClient().mutate({
        mutation: UploadFloorplanBackdropImageDocument,
        variables: { floorplanId, image: imageFile },
      });
      const imageUrl = uploadResult.data?.uploadFloorplanBackdropImage;
      if (!imageUrl) {
        throw new Error("Failed to upload backdrop image");
      }

      if (mode === "replace" && backdrop) {
        const oldImageUrl = backdrop.backdropImagePath;
        floorplannerStore.beginUndoTransaction("replaceBackdropImage");
        floorplannerStore.recordSymbolForUndo(backdrop.id);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropImagePath", imageUrl);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropAlphaMask", undefined);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropRatio", backdropRatio);
        floorplannerStore.updateSymbolProperty(backdrop.id, "backdropSize", backdropSize);
        floorplannerStore.recordSymbolChange(backdrop.id, "modify");
        floorplannerStore.pushToUndoStack();
        if (oldImageUrl && oldImageUrl !== imageUrl) {
          await deleteUploadedBackdrop(floorplanId, oldImageUrl);
        }
      } else {
        const [centerX, centerY] = editorStore.getVisibleCenter();
        const id = floorplannerStore.addSymbol("backdropImage", [centerX, centerY], {
          backdropImagePath: imageUrl,
          backdropRatio,
          backdropSize,
          locked: false,
          zIndex: -1,
        });
        floorplannerStore.setDirty();
        const symbol = floorplannerStore.symbolsMap.get(id);
        if (symbol) {
          editorStore.clearSelections();
          floorplannerStore.updateSymbolProperty(id, "selected", true);
          editorStore.addSelection(symbol);
        }

        // Optionally persist the same image as a user-level upload (UserItem)
        // so it appears in the user's library. Failures here must not block the
        // floorplan flow, hence the localized try/catch.
        if (addToUploads && imageFile) {
          try {
            await getClient().mutate({
              mutation: AddUserItemDocument,
              variables: {
                image: imageFile,
                removeBG: false,
                tags: ["floorplan"],
              },
            });
          } catch (error) {
            console.warn("Could not add backdrop image to user uploads", error);
          }
        }
      }

      invalidate();
      onClose();
    } catch (error) {
      console.error("Error saving backdrop image", error);
    } finally {
      setIsLoading(false);
    }
  };

  const pointHandle = (point: Point, kind: "start" | "end") => {
    const c = toContainerCoords(point.x, point.y);
    return (
      <div
        className="group absolute h-4 w-4"
        style={{ left: `${c.x}px`, top: `${c.y}px`, transform: "translate(-50%, -50%)" }}
      >
        <div
          className="h-full w-full cursor-move rounded-full bg-boniYellowPrimary opacity-0 transition-opacity group-hover:opacity-100"
          onMouseDown={(event) => {
            event.stopPropagation();
            setIsDragging(true);
            setDragPoint(kind);
          }}
          onClick={(event) => event.stopPropagation()}
        />
      </div>
    );
  };

  const doneDisabled =
    mode === "erase"
      ? isLoading || !backdropImage || !imageSize.width || !hasErased
      : isLoading ||
        !backdropImage ||
        !startPoint ||
        !endPoint ||
        ((mode === "add" || mode === "replace") && !backdropFile);
  const previousDistance = backdrop?.backdropSize;
  const previousRatio = backdrop?.backdropRatio;

  const title =
    mode === "add"
      ? t("uploads.addBackgroundImage")
      : mode === "replace"
        ? t("backdrop.replaceBackgroundImage")
        : mode === "erase"
          ? t("backdrop.editBackgroundImage")
          : t("backdrop.resetReference");
  const imageLayout = getImageLayout();
  const eraserCursorSize = eraserSize * zoomLevel;
  const eraserCursorCoords = eraserCursorPoint
    ? toContainerCoords(eraserCursorPoint.x, eraserCursorPoint.y)
    : null;
  const eraserCursorFill = eraserFillMode === "color" ? `${eraserFillColor}66` : "rgba(255,255,255,0.2)";
  const steps =
    mode === "erase"
      ? [
        { id: "erase" as const, icon: FiEdit3, title: t("backdrop.eraserTitle"), body: t("backdrop.eraserBody") },
      ]
      : [
        { id: "upload" as const, icon: FiUploadCloud, title: t("backdrop.stepUpload"), body: t("backdrop.chooseImage") },
        { id: "start" as const, icon: FiMousePointer, title: t("backdrop.stepStart"), body: t("backdrop.setFirstPoint") },
        { id: "end" as const, icon: FiMousePointer, title: t("backdrop.stepEnd"), body: t("backdrop.setSecondPoint") },
        { id: "distance" as const, icon: FiEdit3, title: t("backdrop.stepDistance"), body: t("backdrop.enterRealLength") },
      ];

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-[900px] flex-col rounded-lg bg-[#f7f6f0] shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h2 className="font-['DM Sans'] text-xl">{title}</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={handleClose} disabled={isLoading}>
            <IoClose size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className={`mb-5 grid gap-2 ${mode === "erase" ? "grid-cols-1" : "grid-cols-4"}`}>
            {steps.map((step) => {
              const Icon = step.icon;
              const active = activeStep === step.id;
              const disabled = step.id !== "upload" && !backdropImage;
              return (
                <button
                  type="button"
                  key={step.id}
                  className={`flex min-h-[70px] items-center gap-3 rounded border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active ? "border-boniYellowPrimary bg-boniYellowPrimary" : "border-[#dedbd2] bg-white hover:bg-gray-50"}`}
                  onClick={() => handleStepClick(step.id)}
                  disabled={disabled}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded ${active ? "bg-blackplum text-white" : "bg-[#f7f6f0] text-gray-700"}`}>
                    <Icon size={17} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-5 text-black">{step.title}</span>
                    <span className="block text-xs leading-4 text-gray-500">{step.body}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative flex">
            <div
              ref={imageContainerRef}
              className="relative select-none overflow-auto rounded bg-white"
              style={{
                width: `${containerWidth}px`,
                height: `${containerHeight}px`,
                cursor: activeStep === "erase" ? "none" : startPoint && endPoint ? "default" : "crosshair",
              }}
              onClick={handleImageClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleImageChange}
              />

              {backdropImage ? (
                <div
                  className="relative"
                  style={{
                    width: `${imageLayout.stageWidth}px`,
                    height: `${imageLayout.stageHeight}px`,
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: `${imageLayout.offsetX}px`,
                      top: `${imageLayout.offsetY}px`,
                      width: `${imageLayout.scaledWidth}px`,
                      height: `${imageLayout.scaledHeight}px`,
                      backgroundColor: "#ffffff",
                      backgroundImage:
                        "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)",
                      backgroundPosition: "0 0, 0 12px, 12px -12px, -12px 0px",
                      backgroundSize: "24px 24px",
                    }}
                  />
                  <canvas
                    ref={canvasRef}
                    aria-label={t("backdrop.floorPlanBackdrop")}
                    onPointerDown={handleErasePointerDown}
                    onPointerMove={handleErasePointerMove}
                    onPointerUp={handleErasePointerEnd}
                    onPointerCancel={handleErasePointerEnd}
                    onPointerLeave={handleErasePointerEnd}
                    style={{
                      position: "absolute",
                      left: `${imageLayout.offsetX}px`,
                      top: `${imageLayout.offsetY}px`,
                      maxWidth: "none",
                      maxHeight: "none",
                      width: `${imageLayout.scaledWidth}px`,
                      height: `${imageLayout.scaledHeight}px`,
                      display: "block",
                      pointerEvents: activeStep === "erase" ? "auto" : "none",
                      touchAction: "none",
                    }}
                  />

                  {activeStep === "erase" && eraserCursorCoords && (
                    <div
                      aria-hidden="true"
                      className="absolute rounded-full border border-black bg-white/20 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
                      style={{
                        left: `${eraserCursorCoords.x}px`,
                        top: `${eraserCursorCoords.y}px`,
                        width: `${eraserCursorSize}px`,
                        height: `${eraserCursorSize}px`,
                        backgroundColor: eraserCursorFill,
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {activeStep !== "erase" && startPoint && !endPoint && cursorPos && (
                    <div className="h-[2px] bg-boniYellowPrimary opacity-50" style={getLineStyle(cursorPos)}>
                      <div className="absolute -left-px h-4 w-[2px] -translate-y-1/2 bg-boniYellowPrimary" />
                      <div className="absolute right-0 h-4 w-[2px] -translate-y-1/2 bg-boniYellowPrimary" />
                    </div>
                  )}

                  {activeStep !== "erase" && startPoint && pointHandle(startPoint, "start")}
                  {activeStep !== "erase" && endPoint && pointHandle(endPoint, "end")}

                  {activeStep !== "erase" && startPoint && endPoint && (
                    <div className="h-[2px] bg-boniYellowPrimary" style={getLineStyle(endPoint)}>
                      <div className="absolute -left-px h-4 w-[2px] -translate-y-1/2 bg-boniYellowPrimary" />
                      <div className="absolute right-0 h-4 w-[2px] -translate-y-1/2 bg-boniYellowPrimary" />
                      <div
                        className="absolute"
                        style={{
                          left: "50%",
                          top: "var(--label-offset-y)",
                          transform: "translate(-50%, -50%) rotate(var(--label-rotation))",
                        }}
                      >
                        {isEditingLabel ? (
                          <input
                            autoFocus
                            className="rounded bg-white px-1 text-xs shadow"
                            size={measurementValue.length + 3}
                            value={`${measurementValue}mm`}
                            onChange={(event) => setMeasurementValue(event.target.value.replace(/[^\d.]/g, ""))}
                            onFocus={(event) => event.currentTarget.select()}
                            onBlur={() => setIsEditingLabel(false)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setIsEditingLabel(false);
                            }}
                            onClick={(event) => event.stopPropagation()}
                          />
                        ) : (
                          <span
                            className="inline-block cursor-pointer text-xs text-black"
                            onClick={(event) => {
                              event.stopPropagation();
                              setIsEditingLabel(true);
                            }}
                          >
                            {measurementValue}mm
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  className="flex h-full w-full flex-col items-center justify-center text-gray-500 hover:bg-gray-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="mb-3 flex h-12 w-12 items-center justify-center rounded bg-[#f7f6f0] text-black">
                    <FiUploadCloud size={24} />
                  </span>
                  <span className="text-sm font-medium text-black">{t("backdrop.uploadFloorPlanImage")}</span>
                  <span className="mt-1 text-xs text-gray-500">{t("backdrop.fileTypes")}</span>
                </button>
              )}
            </div>

            <div className="ml-3 flex w-[150px] flex-col gap-2">
              {mode === "erase" && (
                <>
                  <div className="rounded border border-[#dedbd2] bg-white p-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        className="inline-flex h-8 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                        onClick={(event) => {
                          event.stopPropagation();
                          undoEdit();
                        }}
                        disabled={isLoading || editHistory.index <= 0}
                        title={t("backdrop.undo")}
                      >
                        <FiRotateCcw size={15} />
                      </button>
                      <button
                        className="inline-flex h-8 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                        onClick={(event) => {
                          event.stopPropagation();
                          redoEdit();
                        }}
                        disabled={isLoading || editHistory.index >= editHistory.length - 1}
                        title={t("backdrop.redo")}
                      >
                        <FiRotateCw size={15} />
                      </button>
                    </div>
                    <div className="mt-2">
                      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">{t("common.color")}</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          className={`h-10 rounded border px-2 text-xs font-medium ${eraserFillMode === "transparent" ? "border-boniYellowPrimary bg-boniYellowPrimary text-blackplum" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}
                          onClick={() => {
                            setEraserFillMode("transparent");
                          }}
                          disabled={!backdropImage || isLoading}
                        >
                          {t("backdrop.erase")}
                        </button>
                        <button
                          type="button"
                          className={`flex h-10 flex-col items-stretch justify-center rounded border px-2 text-xs font-medium ${eraserFillMode === "color" ? "border-boniYellowPrimary bg-boniYellowPrimary text-blackplum" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}
                          onClick={() => {
                            setEraserFillMode("color");
                            window.setTimeout(() => eraserColorInputRef.current?.click(), 0);
                          }}
                          disabled={!backdropImage || isLoading}
                          title={eraserFillColor.toUpperCase()}
                        >
                          <span className="text-center leading-4">{t("common.color")}</span>
                          <span
                            className="mt-0.5 h-1.5 rounded-full border border-gray-300"
                            style={{ backgroundColor: eraserFillColor }}
                          />
                        </button>
                      </div>
                      <input
                        ref={eraserColorInputRef}
                        type="color"
                        className="sr-only"
                        value={eraserFillColor}
                        onChange={(event) => {
                          setEraserFillColor(event.target.value);
                          setEraserFillMode("color");
                        }}
                      />
                    </div>
                    <div className="mt-2">
                      <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-gray-500">
                        <span>{t("common.size")}</span>
                        <span className="font-normal normal-case tracking-normal text-gray-700">{eraserSize}px</span>
                      </div>
                      <input
                        className="w-full accent-boniYellowPrimary"
                        type="range"
                        min={8}
                        max={120}
                        step={2}
                        value={eraserSize}
                        onChange={(event) => setEraserSize(Number(event.target.value))}
                        disabled={!backdropImage || isLoading}
                      />
                    </div>
                  </div>
                </>
              )}
              {mode === "recalibrate" && (
                <div className="rounded border border-[#dedbd2] bg-white p-3 text-xs text-gray-600">
                  <div className="font-medium text-gray-900">{t("backdrop.currentSetting")}</div>
                  <div className="mt-2 flex justify-between gap-2">
                    <span>{t("common.distance")}</span>
                    <span className="text-right text-gray-900">{formatMillimeters(previousDistance, t("backdrop.notSet"))}</span>
                  </div>
                  <div className="mt-1 flex justify-between gap-2">
                    <span>{t("backdrop.imageSpan")}</span>
                    <span className="text-right text-gray-900">{formatPercent(previousRatio, t("backdrop.notSet"))}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  className="inline-flex h-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={(event) => {
                    event.stopPropagation();
                    setZoomLevel((prev) => Math.min(prev + 0.1, 5));
                  }}
                  disabled={!backdropImage}
                  title={t("common.zoomIn")}
                >
                  <FiZoomIn size={16} />
                </button>
                <button
                  className="inline-flex h-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={(event) => {
                    event.stopPropagation();
                    setZoomLevel((prev) => Math.max(prev - 0.1, 0.2));
                  }}
                  disabled={!backdropImage}
                  title={t("common.zoomOut")}
                >
                  <FiZoomOut size={16} />
                </button>
              </div>
              {(mode === "add" || mode === "replace") && backdropImage && (
                <button
                  className="inline-flex h-11 items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3 text-sm shadow-sm hover:bg-gray-50"
                  onClick={(event) => {
                    event.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  <FiUploadCloud size={16} />
                  {t("backdrop.replaceFile")}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <button className="rounded bg-white px-4 py-2 text-sm text-black hover:bg-gray-50" onClick={handleClose}>
              {t("common.back")}
            </button>
            {mode === "add" && (
              <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className={`${BONI_CHECKBOX_CLASSNAME} h-4 w-4`}
                  checked={addToUploads}
                  onChange={(event) => setAddToUploads(event.target.checked)}
                  disabled={isLoading}
                />
                {t("backdrop.addToMyUploads")}
              </label>
            )}
            <button
              className="rounded bg-black px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50"
              disabled={doneDisabled}
              onClick={handleDone}
            >
              {isLoading ? t("common.saving") : t("common.done")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
